/**
 * Contatos comerciais recebidos pelo formulário da landing /para-empresas.
 *
 * Sem paginação, seguindo a convenção do resto do painel: a lista vem
 * inteira e é filtrada no cliente. Se o volume passar de ~2 mil leads,
 * isso precisa virar paginação server-side (o back já aceita ?status=).
 */
import { useMemo, useState } from 'react'
import { AlertTriangle, Download, Inbox, MessageSquare, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import { downloadContactsCsv } from '@/lib/contactsCsv'
import { getErrorMessage } from '@/utils/error'
import {
  useCompanyContacts,
  useUpdateCompanyContact,
  type CompanyContact,
  type ContactStatus,
} from '@/services/super-admin/contacts.service'

const STATUS_META: Record<ContactStatus, { label: string; className: string }> = {
  novo: { label: 'Novo', className: 'bg-primary/15 text-primary border-primary/30' },
  em_contato: { label: 'Em contato', className: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  convertido: {
    label: 'Convertido',
    className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  },
  descartado: { label: 'Descartado', className: 'bg-muted text-muted-foreground border-border' },
}

const STATUS_ORDER: ContactStatus[] = ['novo', 'em_contato', 'convertido', 'descartado']

const formatDate = (iso: string) => {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR')
}

export function ContactsManagement() {
  const { data: contacts, isLoading, isError } = useCompanyContacts()
  const updateContact = useUpdateCompanyContact()
  const { toast } = useToast()

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ContactStatus | undefined>(undefined)
  const [editingNotes, setEditingNotes] = useState<CompanyContact | null>(null)
  const [notesDraft, setNotesDraft] = useState('')

  // Contador sobre a lista COMPLETA — o filtro não pode esconder pendência.
  const novos = useMemo(() => contacts?.filter((c) => c.status === 'novo').length ?? 0, [contacts])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (contacts ?? []).filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false
      if (!term) return true
      return (
        c.name.toLowerCase().includes(term) ||
        c.company.toLowerCase().includes(term) ||
        c.email.toLowerCase().includes(term)
      )
    })
  }, [contacts, search, statusFilter])

  const handleStatusChange = async (contact: CompanyContact, status: ContactStatus) => {
    try {
      await updateContact.mutateAsync({ id: contact.id, status })
    } catch (error: unknown) {
      toast({
        title: 'Erro',
        description: getErrorMessage(error, 'Não foi possível atualizar o status'),
        variant: 'destructive',
      })
    }
  }

  const openNotes = (contact: CompanyContact) => {
    setEditingNotes(contact)
    setNotesDraft(contact.notes ?? '')
  }

  const handleSaveNotes = async () => {
    if (!editingNotes) return
    try {
      await updateContact.mutateAsync({
        id: editingNotes.id,
        notes: notesDraft.trim() || null,
      })
      setEditingNotes(null)
      toast({ title: 'Anotação salva' })
    } catch (error: unknown) {
      toast({
        title: 'Erro',
        description: getErrorMessage(error, 'Não foi possível salvar a anotação'),
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50">
        <CardHeader>
          <Skeleton className="h-5 w-56" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="glass-card border-border/50">
        <CardContent className="py-8 text-center text-muted-foreground">
          Não foi possível carregar os contatos.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="glass-card border-border/50">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="w-5 h-5" />
              Contatos de Empresas
              {novos > 0 && (
                <Badge className={STATUS_META.novo.className}>
                  {novos} {novos === 1 ? 'novo' : 'novos'}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Leads recebidos pelo formulário da landing /para-empresas
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={filtered.length === 0}
            onClick={() => downloadContactsCsv(filtered)}
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por empresa, nome ou e-mail..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={statusFilter ?? 'all'}
            onValueChange={(v) => setStatusFilter(v === 'all' ? undefined : (v as ContactStatus))}
          >
            <SelectTrigger className="w-full sm:w-52">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {STATUS_ORDER.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_META[status].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filtered.length > 0 ? (
          <div className="rounded-md border border-border/50 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Recebido em</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Anotações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {contact.company}
                        {contact.notifiedAt === null && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertTriangle
                                className="w-4 h-4 text-amber-500 shrink-0"
                                aria-label="Notificação por e-mail não foi entregue"
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              A notificação por e-mail não foi entregue ao time
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>{contact.name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                        {contact.email}
                      </a>
                    </TableCell>
                    <TableCell>{contact.phone || '—'}</TableCell>
                    <TableCell>{formatDate(contact.createdAt)}</TableCell>
                    <TableCell>
                      <Select
                        value={contact.status}
                        disabled={updateContact.isPending}
                        onValueChange={(v) => handleStatusChange(contact, v as ContactStatus)}
                      >
                        <SelectTrigger className="w-36 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_ORDER.map((status) => (
                            <SelectItem key={status} value={status}>
                              {STATUS_META[status].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2"
                        onClick={() => openNotes(contact)}
                      >
                        <MessageSquare className="w-4 h-4" />
                        {contact.notes ? 'Ver' : 'Adicionar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>
              {contacts && contacts.length > 0
                ? 'Nenhum contato encontrado para os filtros selecionados.'
                : 'Nenhum contato recebido ainda.'}
            </p>
          </div>
        )}
      </CardContent>

      <Dialog open={editingNotes !== null} onOpenChange={(open) => !open && setEditingNotes(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anotações internas</DialogTitle>
            <DialogDescription>
              {editingNotes?.company} — {editingNotes?.name}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="Registre o andamento da conversa com esse contato..."
            rows={6}
            maxLength={2000}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNotes(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveNotes} disabled={updateContact.isPending}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
